<script>
import { Loader } from '@googlemaps/js-api-loader'
import moment from 'moment'
import { mapState, mapMutations, mapActions } from 'vuex'
import { journeyTypeOptions } from '@/store/options'
import { FormErrors, AppError, AutoComplete, MapDestination } from '../components'

export default {
  components: {
    FormErrors,
    AppError,
    AutoComplete,
    MapDestination,
  },

  props: {
    customLogo: {
      type: String,
      required: false,
    },
    destination: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      journeyTypeOptions,
      journeyTypeModel: 'single',
      showMapDestination: false,
      google: null,
    }
  },

  computed: {
    ...mapState(['form', 'journeyType', 'errors', 'defaultError', 'isLoading']),
    minimumDate() {
      return moment().toISOString()
    },
    maximumDate() {
      return moment().add(6, 'months').toISOString()
    },
    returnMinimumDate() {
      return moment(this.form.out_time).toISOString()
    },
    logo() {
      return require(`../static/${this.$whitelabel.brandLogoName}`)
    },
    destinationLocked() {
      return this.destination && this.destination.length
    },
  },

  async mounted() {
    await new Loader({
      apiKey: process.env.VUE_APP_GOOGLE_APIKEY,
      libraries: ['places', 'geometry'],
    })
      .load()
      .then((google) => {
        this.google = google
      })
      .catch((e) => {
        // do something
      })
  },
  created() {
    this.form.destination = this.destination
  },
  methods: {
    ...mapMutations(['setError', 'setDefaultError']),
    ...mapActions(['searchQuery']),
    switchFromTo() {
      if (this.isLoading) return

      const currentOrigin = this.form.origin
      const currentDestination = this.form.destination

      this.form.destination = currentOrigin
      this.form.origin = currentDestination
    },
    async search() {
      this.setDefaultError(false)
      const response = await this.searchQuery({
        params: this.prepareInput(),
        router: this.$router,
      })
      // redirect the user to the result page
      if (response !== false) {
        const redirectTo = `${process.env.VUE_APP_BRAND_URL}search/${response}/results`
        window.open(redirectTo)
      }
    },
    onFocus(field) {
      this.setError({
        field,
        error: false,
      })
    },
    prepareInput() {
      const form = { ...this.form, has_car_access: true }
      // delete form.arrive_time
      form.out_time = this.formatDate(this.form.out_time)
      if (form.return_time) {
        form.return_time = this.formatDate(this.form.return_time)
      } else {
        delete form.return_time
      }

      return form
    },
    formatDate(date) {
      return moment(date).format('YYYY/MM/DD HH:mm:ss')
    },
    onJourneyTypeChange(e) {
      if (e === 'single') {
        this.form.return_time = null
      }
    },
    onSetDestination(address) {
      this.form.destination = address
      this.showMapDestination = false
    },
  },
}
</script>

<template>
  <div
    id="main"
    class="jpw mt-3"
    role="main"
  >
    <map-destination
      v-if="showMapDestination"
      :google="google"
      :center="$whitelabel.mapCenter"
      @showForm="showMapDestination = false"
      @setSelectedDestionation="onSetDestination"
    ></map-destination>
    <form
      v-else
      ref="form"
      label-position="left"
      class="userform"
    >
      <div class="card mx-auto max-width-4 bg-widget-primary">
        <div class="card-body px-lg-3 text-left">
          <img
            v-if="customLogo"
            :src="customLogo"
            class="top-logo mb-2"
            :alt="$whitelabel.brand"
          />
          <svg-icon
            v-else-if="$whitelabel.widgetLogoName"
            class="top-logo mb-3"
            :name="$whitelabel.widgetLogoName"
          />
          <div
            v-else
            class="text-logo"
          >
            {{ $whitelabel.brand }}
          </div>
          <form-errors
            :errors="errors"
            aria-invalid="true"
          ></form-errors>
          <app-error
            :error="defaultError"
            aria-invalid="true"
          ></app-error>
          <div
            id="from-to-container"
            class="d-flex from-to-container"
            :class="{ 'has-error': errors.origin || errors.destination }"
          >
            <div class="flex-fill pr-3">
              <div
                class="d-flex"
                @click="onFocus('origin')"
              >
                <div class="from-to-input-group">
                  <p class="from-to-input-group-text"><strong>From</strong></p>
                </div>
                <div class="flex-fill">
                  <auto-complete
                    v-if="google"
                    v-model="form.origin"
                    :google="google"
                    aria-label="origin location input"
                    placeholder="Enter origin"
                  ></auto-complete>
                </div>
              </div>
              <div id="from-to-input-separator"></div>
              <div
                class="d-flex"
                @click="onFocus('destination')"
              >
                <div class="from-to-input-group">
                  <p class="from-to-input-group-text"><strong>To</strong></p>
                </div>
                <div class="flex-fill">
                  <auto-complete
                    v-if="google"
                    v-model="form.destination"
                    :google="google"
                    aria-label="destination location input"
                    :input-disabled="destinationLocked"
                    placeholder="Enter destination"
                    :destination="true"
                    @showMapDestination="showMapDestination = true"
                  ></auto-complete>
                </div>
              </div>
            </div>
            <div>
              <div
                v-if="!destinationLocked"
                id="switch-from-to"
                class="c-pointer"
                @click="switchFromTo"
              >
                <svg-icon
                  name="switch-from-to-icons"
                  width="9"
                />
              </div>
            </div>
          </div>
          <div class="d-flex">
            <b-form-group>
              <legend class="sr-only">select single journey or return journey</legend>
              <b-form-radio-group
                id="journey-type-buttons"
                v-model="journeyTypeModel"
                :disabled="isLoading"
                :options="journeyTypeOptions"
                buttons
                name="journey-type"
                @change="onJourneyTypeChange"
              ></b-form-radio-group>
            </b-form-group>
          </div>
          <div class="mt-3">
            <b-form-group
              label="Outbound"
              class="planner-datepicker"
            >
              <div class="bv-no-focus-ring">
                <svg-icon
                  class="time-picker-calendar c-pointer calendar-icon-home"
                  name="time-picker-calendar-icon"
                  width="18"
                ></svg-icon>
                <datetime
                  v-model="form.out_time"
                  :week-start="1"
                  :min-datetime="minimumDate"
                  :max-datetime="maximumDate"
                  :minute-step="5"
                  type="datetime"
                  input-id="outbound-input"
                  placeholder="choose a date and time"
                  input-class="form-control ajp-form-control datetime-picker-control c-pointer"
                  class="theme-red"
                  :class="{ 'has-error': errors.out_time }"
                  aria-label="date-time-picker"
                  @focus="onFocus('out_time')"
                ></datetime>
              </div>
            </b-form-group>
            <b-form-group
              v-if="journeyTypeModel == 'return'"
              label="Return"
              label-for="return"
              class="planner-datepicker"
            >
              <div class="bv-no-focus-ring">
                <svg-icon
                  class="time-picker-calendar c-pointer calendar-icon-home"
                  name="time-picker-calendar-icon"
                  width="18"
                />
                <datetime
                  v-model="form.return_time"
                  :week-start="1"
                  :min-datetime="returnMinimumDate"
                  :max-datetime="maximumDate"
                  :minute-step="5"
                  type="datetime"
                  input-id="return-input"
                  placeholder="choose a date and time"
                  input-class="form-control ajp-form-control datetime-picker-control c-pointer"
                  class="datetime-picker theme-red"
                  :class="{ 'has-error': errors.return_time }"
                  aria-label="date-time-picker"
                  @focus="onFocus('return_time')"
                ></datetime>
              </div>
            </b-form-group>
          </div>
          <b-button
            class="full-btn mt-1 mb-1"
            :disabled="isLoading"
            @click="search"
          >
            <span v-if="!isLoading">
              Find Routes & Times
              <svg-icon
                v-if="$whitelabel.brand == 'lochlomond'"
                class="arrow-right-find"
                name="forward"
              ></svg-icon>
            </span>
            <b-spinner
              v-if="isLoading"
              type="grow"
              label="Spinning"
              style="width: 24px; height: 24px"
              class="app-spinner-white"
            ></b-spinner>
          </b-button>
        </div>
      </div>
    </form>
  </div>
</template>
